import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  List,
  ListItem,
  Spinner,
  Tag,
  Text,
  VStack,
} from '@chakra-ui/react'
import { LuBuilding2 } from 'react-icons/lu'
import { useCallback, useEffect, useRef, useState } from 'react'
import { publicAxios } from '../../../api/axios'

export interface Organization {
  id: string
  officialName: string
  shortName: string | null
  type: 'chu' | 'ch' | 'private_lab' | 'clinic' | 'research_center' | 'other'
  city: string | null
  region: string | null
  slug: string
}

interface OrganizationSearchProps {
  label?: string
  placeholder?: string
  isInvalid?: boolean
  errorMessage?: string
  /** Called when a known organization is selected (sends organizationId) */
  onOrganizationSelect: (organizationId: string | null, companyName: string) => void
  /** Initial displayed value (e.g. from loaded user profile) */
  initialValue?: string
  /** Initial organizationId (e.g. from loaded user profile) */
  initialOrganizationId?: string | null
}

const TYPE_LABELS: Record<Organization['type'], string> = {
  chu: 'CHU',
  ch: 'CH',
  private_lab: 'Labo privé',
  clinic: 'Clinique',
  research_center: 'Recherche',
  other: 'Autre',
}

const TYPE_COLORS: Record<Organization['type'], string> = {
  chu: 'purple',
  ch: 'blue',
  private_lab: 'green',
  clinic: 'orange',
  research_center: 'teal',
  other: 'gray',
}

const AUTRE_OPTION_ID = '__autre__'

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debounced
}

export default function OrganizationSearch({
  label = 'Établissement',
  placeholder = 'Rechercher un CHU, hôpital...',
  isInvalid,
  errorMessage,
  onOrganizationSelect,
  initialValue,
  initialOrganizationId,
}: OrganizationSearchProps) {
  const [inputValue, setInputValue] = useState(initialValue || '')
  const [results, setResults] = useState<Organization[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null)
  const [showCustomInput, setShowCustomInput] = useState(false)
  const [customName, setCustomName] = useState('')
  const [activeIndex, setActiveIndex] = useState(-1)

  const debouncedQuery = useDebounce(inputValue, 300)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const customInputRef = useRef<HTMLInputElement>(null)
  const isInitialized = useRef(false)
  // Last custom name pushed up via onOrganizationSelect — guards against a double
  // commit (e.g. Enter then blur with the same value firing two saves).
  const lastCommittedRef = useRef<string | null>(null)

  // On initial load with an initialOrganizationId, mark as already selected
  useEffect(() => {
    if (isInitialized.current) return
    isInitialized.current = true

    if (initialOrganizationId && initialValue) {
      // User already has a linked org; treat as selected
      setInputValue(initialValue)
    } else if (!initialOrganizationId && initialValue) {
      // Free-text entry — show custom input, pre-filled. Seed the committed ref with
      // the loaded value so opening then blurring without editing does NOT re-save.
      setShowCustomInput(true)
      setCustomName(initialValue)
      setInputValue('Autre / Organisation non listée')
      lastCommittedRef.current = initialValue.trim()
    }
  }, [initialOrganizationId, initialValue])

  // Search when debounced query changes
  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2 || selectedOrg) {
      setResults([])
      return
    }
    if (showCustomInput) return

    setIsLoading(true)
    publicAxios
      .get<Organization[]>(`organizations/search?q=${encodeURIComponent(debouncedQuery)}`)
      .then((res) => {
        setResults(res.data)
        setIsOpen(true)
        setActiveIndex(-1)
      })
      .catch(() => setResults([]))
      .finally(() => setIsLoading(false))
  }, [debouncedQuery, selectedOrg, showCustomInput])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = useCallback(
    (org: Organization | typeof AUTRE_OPTION_ID) => {
      if (org === AUTRE_OPTION_ID) {
        setSelectedOrg(null)
        setShowCustomInput(true)
        setInputValue('Autre / Organisation non listée')
        setIsOpen(false)
        setResults([])
        setCustomName('')
        lastCommittedRef.current = null
        // Do NOT push an empty value up here: the parent auto-saves on every
        // onOrganizationSelect and would wipe the name + collapse the editor before
        // the user can type. We commit only once a name is entered (blur / Enter).
        setTimeout(() => customInputRef.current?.focus(), 0)
      } else {
        setSelectedOrg(org)
        setShowCustomInput(false)
        setInputValue(org.officialName)
        setIsOpen(false)
        setResults([])
        onOrganizationSelect(org.id, org.officialName)
      }
    },
    [onOrganizationSelect],
  )

  const handleClear = () => {
    setSelectedOrg(null)
    setShowCustomInput(false)
    setInputValue('')
    setCustomName('')
    setResults([])
    setIsOpen(false)
    onOrganizationSelect(null, '')
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) return
    const total = results.length + 1 // +1 for "Autre" option

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((prev) => (prev + 1) % total)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((prev) => (prev - 1 + total) % total)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (activeIndex >= 0 && activeIndex < results.length) {
        handleSelect(results[activeIndex])
      } else if (activeIndex === results.length) {
        handleSelect(AUTRE_OPTION_ID)
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false)
      setActiveIndex(-1)
    }
  }

  // Typing only updates local state — no save on each keystroke (that used to fire
  // the parent's auto-save, which collapsed the editor after the first character).
  const handleCustomNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomName(e.target.value)
    lastCommittedRef.current = null
  }

  // Commit the free-text name up to the parent (which persists it). Called on blur
  // or Enter, so the user can type the whole name before anything is saved.
  const commitCustomName = () => {
    const name = customName.trim()
    if (!name || lastCommittedRef.current === name) return
    lastCommittedRef.current = name
    onOrganizationSelect(null, name)
  }

  return (
    <Box ref={containerRef} position="relative" w="100%">
      <FormControl isInvalid={isInvalid}>
        {label && (
          <FormLabel display="flex" alignItems="center" gap={2} fontSize="sm" fontWeight="medium">
            {label}
          </FormLabel>
        )}
        <InputGroup>
          <Input
            ref={inputRef}
            value={inputValue}
            placeholder={placeholder}
            borderRadius="lg"
            borderColor={isInvalid ? 'red.500' : selectedOrg ? 'purple.400' : 'gray.300'}
            _hover={{ borderColor: isInvalid ? 'red.600' : 'gray.400' }}
            _focus={{
              borderColor: isInvalid ? 'red.500' : 'brand.purple',
              boxShadow: isInvalid ? '0 0 0 1px #FC8181' : '0 0 0 1px #6D3CFE',
            }}
            readOnly={!!selectedOrg || showCustomInput}
            onChange={(e) => {
              if (!selectedOrg && !showCustomInput) {
                setInputValue(e.target.value)
              }
            }}
            onFocus={() => {
              if (!selectedOrg && !showCustomInput && inputValue.length >= 2) {
                setIsOpen(true)
              }
            }}
            onKeyDown={handleKeyDown}
            pr={selectedOrg || showCustomInput ? '2.5rem' : undefined}
            cursor={selectedOrg || showCustomInput ? 'default' : 'text'}
            bg={selectedOrg ? 'purple.50' : undefined}
          />
          {(selectedOrg || showCustomInput) && (
            <InputRightElement>
              <Box
                as="button"
                type="button"
                onClick={handleClear}
                fontSize="xs"
                color="gray.400"
                _hover={{ color: 'red.400' }}
                title="Effacer"
              >
                ✕
              </Box>
            </InputRightElement>
          )}
          {isLoading && (
            <InputRightElement>
              <Spinner size="xs" color="purple.400" />
            </InputRightElement>
          )}
        </InputGroup>

        {/* Dropdown */}
        {isOpen && (results.length > 0 || !isLoading) && (
          <Box
            position="absolute"
            top="100%"
            left={0}
            right={0}
            zIndex={1000}
            bg="white"
            borderRadius="lg"
            boxShadow="xl"
            border="1px solid"
            borderColor="gray.200"
            mt={1}
            maxH="280px"
            overflowY="auto"
          >
            <List>
              {results.map((org, idx) => (
                <ListItem
                  key={org.id}
                  px={3}
                  py={2.5}
                  cursor="pointer"
                  bg={activeIndex === idx ? 'purple.50' : 'transparent'}
                  _hover={{ bg: 'purple.50' }}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    handleSelect(org)
                  }}
                  onMouseEnter={() => setActiveIndex(idx)}
                  borderBottom="1px solid"
                  borderColor="gray.100"
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box as={LuBuilding2} boxSize={3.5} color="gray.400" flexShrink={0} />
                    <Box flex={1} minW={0}>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.800" noOfLines={1}>
                        {org.officialName}
                      </Text>
                      {org.city && (
                        <Text fontSize="xs" color="gray.500">
                          {org.city}{org.region ? ` · ${org.region}` : ''}
                        </Text>
                      )}
                    </Box>
                    <Tag
                      size="sm"
                      colorScheme={TYPE_COLORS[org.type]}
                      flexShrink={0}
                      fontSize="xs"
                    >
                      {TYPE_LABELS[org.type]}
                    </Tag>
                  </Box>
                </ListItem>
              ))}

              {/* "Autre" option */}
              <ListItem
                px={3}
                py={2.5}
                cursor="pointer"
                bg={activeIndex === results.length ? 'orange.50' : 'transparent'}
                _hover={{ bg: 'orange.50' }}
                onMouseDown={(e) => {
                  e.preventDefault()
                  handleSelect(AUTRE_OPTION_ID)
                }}
                onMouseEnter={() => setActiveIndex(results.length)}
                borderTop={results.length > 0 ? '1px dashed' : undefined}
                borderColor="gray.200"
              >
                <Text fontSize="sm" color="orange.600" fontWeight="medium">
                  + Autre / Organisation non listée
                </Text>
              </ListItem>
            </List>
          </Box>
        )}

        {isInvalid && errorMessage && (
          <FormErrorMessage fontSize="sm">{errorMessage}</FormErrorMessage>
        )}
      </FormControl>

      {/* Free-text input shown when "Autre" selected */}
      {showCustomInput && (
        <Box mt={3}>
          <FormControl>
            <FormLabel fontSize="xs" color="gray.500" mb={1}>
              Nom de votre établissement
            </FormLabel>
            <Input
              ref={customInputRef}
              value={customName}
              onChange={handleCustomNameChange}
              onBlur={commitCustomName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  commitCustomName()
                }
              }}
              placeholder="Saisissez le nom de votre établissement"
              size="sm"
              borderRadius="md"
              borderColor="orange.300"
              _focus={{
                borderColor: 'orange.500',
                boxShadow: '0 0 0 1px #ED8936',
              }}
            />
          </FormControl>
        </Box>
      )}
    </Box>
  )
}
